import React, {createContext, PropsWithChildren, useContext} from 'react';
import {
  LEGAL_PAGE_TYPE,
  StaticLegalPageRoutes,
} from './localizedUrls/legalPagesRouteKeys';
import {DeprecatedRouteKeys, RouteKey} from './localizedUrls/routeKeys';
import {PageTypeContextProvider, useCurrentPageType} from './PageTypeContext';
import {RedirectsMap} from './redirect.paths';
import {PageTypePathMap} from './route.paths';
import {
  BrowserRouter,
  MemoryRouter,
  ReactRouterProps,
  StaticRouter,
} from './Router';
import {
  LocationStateProvider,
  PathnameProvider,
  SearchProvider,
  usePathname,
} from './useLocation';

export type RouterProviderProps = {
  routesConfig: StaticRoutesConfig;
  routerOptions: ReactRouterProps;
  OverrideRouter?:
    | typeof BrowserRouter
    | typeof StaticRouter
    | typeof MemoryRouter;
};

export type StaticRoutesConfig = {
  pageTypePathsMap: PageTypePathMap;
  redirectsMap: RedirectsMap;
  staticRoutes: Record<Exclude<RouteKey, DeprecatedRouteKeys>, string>;
  legalRoutes: StaticLegalPageRoutes;
  force404?: boolean;
};

export const RoutesConfigContext = createContext<StaticRoutesConfig>(
  new Proxy<StaticRoutesConfig>({} as StaticRoutesConfig, {
    get() {
      throw new Error(
        'Cannot find value for RoutesConfigContext. Did you add a RouterProvider in one of the parent components?',
      );
    },
  }),
);

function useGetStaticRoute() {
  const staticRoutes = useContext(RoutesConfigContext).staticRoutes;
  return (key: Exclude<RouteKey, DeprecatedRouteKeys>) =>
    `/${staticRoutes[key]}`;
}

export const useBasketRoute = () => useGetStaticRoute()(RouteKey.Basket);
export const useBrandsRoute = () => useGetStaticRoute()(RouteKey.Brands);
export const useWishlistRoute = () => useGetStaticRoute()(RouteKey.Wishlist);
export const useSearchRoute = () => '/search'; // we'll unify the search URL to be only in english, see ticket ASFE-7313
export const useImprintRoute = () => useGetStaticRoute()(RouteKey.Imprint);

function useGetStaticLegalRoute() {
  const staticRoutes = useContext(RoutesConfigContext).legalRoutes;
  return (key: LEGAL_PAGE_TYPE) => {
    const route = staticRoutes[key];
    return route ? `/${route}` : null;
  };
}

export const usePrivacyPolicyRoute = () =>
  useGetStaticLegalRoute()(LEGAL_PAGE_TYPE.Privacy)!;

export const useGeneralConditionsReverseSupplyRoute = () =>
  // some locales have no legal route for GeneralConditionsReverseSupply
  useGetStaticLegalRoute()(LEGAL_PAGE_TYPE.GeneralConditionsReverseSupply) ??
  '/';

export const useVoucherConditionPage = () => {
  // Hardcoding this URL since the voucher terms and conditions Page is now removed, and the 404 will redirect the user to the new custom page - ASFE-11108
  return '/voucher-terms-and-conditions';
};

export const useRedirectsMap = () => {
  return useContext(RoutesConfigContext).redirectsMap;
};

export const usePageTypePathsMap = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap;
};

export const useContactRoute = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap.contacts[0] as string;
};

export const useIsPremiumPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'premium';
};

export const useIsArticleDetailPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'articleDetail';
};

export const useIsArticleDetailSoldOutPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'articleDetailSoldOut';
};

export const useIsSearchResultsPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'search';
};

export const useIsStoryPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'story' || pageType === 'storyOverview';
};

export const useIsOrderSuccessPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'orderSuccess';
};

export const useIsLaunchTeaserPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'teaserPage';
};

export const useIsBasketPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'basket';
};

export const useIsNAAPage = () => {
  const NAAPages = [
    'ordersOverview',
    'userProfile',
    'bankData',
    'orderReturn',
    'orderDetails',
  ];
  const pageType = useCurrentPageType();
  return pageType ? NAAPages.includes(pageType) : true;
};

export const useIsCheckoutPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'checkout';
};

export const useIsPayOrderAppPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'payOrderApp';
};

export const useIsWelcomePage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'welcome';
};

export const useIsHelpPage = () => {
  const pageType = useCurrentPageType();
  return pageType?.includes('help');
};

export const useAccountOverviewRoute = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap
    .accountOverview[0] as string;
};

export const useVipLandingPageRoute = () => {
  // Hardcoding this URL since the VIP Page is now removed, and the 404 will redirect the user to the new CMS VIP Page - ASFE-5709
  return '/about-you-vip';
};

export const useOrdersOverviewRoute = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap
    .ordersOverview[0] as string;
};

export const useCheckoutRoute = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap.checkout[0] as string;
};

export const useIsCategoryPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'category';
};
export const useIsOutfitDetailPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'outfitDetail';
};
export const useIsHomePage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'home' || pageType === 'welcome';
};

export const useIsBrowsePage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'home';
};

export const useIsDropPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'dropDetail' || pageType === 'dropsOverview';
};

export const useIsWishlistPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'wishlist';
};
export const useIsCouponLandingPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'couponLandingPage';
};
export const useIsSimilarProductsPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'similarProducts';
};

export const useIsBrandPage = () => {
  const pageType = useCurrentPageType();
  return pageType === 'brandShop';
};

export const useIsMultiBrand = () => {
  const pathname = usePathname();
  const isBrandPage = useIsBrandPage();
  return isBrandPage && pathname.endsWith('/all');
};

export const useBrandsOverviewPage = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap
    .brandsOverview[0] as string;
};

export const useFavouritesPage = () => {
  return useContext(RoutesConfigContext).pageTypePathsMap
    .favourites[0] as string;
};

export function RouterProvider({
  routesConfig,
  routerOptions,
  OverrideRouter = process.browser ? BrowserRouter : StaticRouter,
  children,
}: PropsWithChildren<RouterProviderProps>) {
  return (
    <OverrideRouter force404={routesConfig.force404} {...routerOptions}>
      <SearchProvider>
        <PathnameProvider>
          <LocationStateProvider>
            <RoutesConfigContext.Provider value={routesConfig}>
              <PageTypeContextProvider>{children}</PageTypeContextProvider>
            </RoutesConfigContext.Provider>
          </LocationStateProvider>
        </PathnameProvider>
      </SearchProvider>
    </OverrideRouter>
  );
}
